/**
 * WatchAnalyticsPage · iter147.1
 *
 * Recharts-Frontend für Creator-Watch-Analytics.
 * Feature-Gates:
 *   - Basis (STANDARD): Impressions, Engagement, Tips, Follower/Subs
 *   - Full (PREMIUM):   Retention D1/D7/D30, Publish-Time-Heatmap, Source-Attribution
 * FREE bekommt Upgrade-Hint statt Daten.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Cell, PieChart, Pie, Legend,
} from "recharts";
import { ArrowLeft, BarChart3, Eye, Heart, MessageSquare, Users, Sparkles, Loader2, Lock, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const WINDOWS = [
  { key: 7, label: "7 Tage" },
  { key: 30, label: "30 Tage" },
  { key: 90, label: "90 Tage" },
];

// Heatmap-Farb-Skala (0..1 → hell → dunkel)
function heatColor(v) {
  const alpha = Math.min(1, Math.max(0.05, v));
  return `rgba(10, 10, 10, ${alpha})`;
}

const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function UpgradeHint({ feature, currentPlan }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div
      data-testid={`upgrade-hint-${feature}`}
      className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
    >
      <Lock size={16} className="text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-bold text-amber-900">{t("watchlist.premium_only_hint")}</p>
        <p className="text-xs text-amber-800 mt-1">
          Diese Ansicht (&bdquo;{feature}&ldquo;) gehört zu Premium. Dein aktueller Tarif: <b>{currentPlan}</b>.
        </p>
        <button
          data-testid={`upgrade-cta-${feature}`}
          onClick={() => navigate("/studio/helper?tab=tariffs")}
          className="mt-3 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] flex items-center gap-2"
        >
          <Sparkles size={12} /> Tarif ansehen &amp; freischalten
        </button>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#E5E5E5] p-4 bg-white">
      <div className="flex items-center gap-2 text-[#666] mb-2">
        <Icon size={14} />
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black text-[#0A0A0A]">{value}</p>
      {sub && <p className="text-[10px] text-[#999] mt-1">{sub}</p>}
    </div>
  );
}

export default function WatchAnalyticsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || user === false) {
      navigate("/login?next=/studio/watch-analytics");
      return;
    }
    load();
  }, [user, windowDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/analytics/creator/me?window=${windowDays}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Analytics konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="watch-analytics-loading" className="min-h-screen flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-[#999]" />
      </div>
    );
  }

  const planCode = data?.plan_code || "FREE";
  const hasBasic = ["STANDARD", "PREMIUM"].includes(planCode);
  const hasFull = planCode === "PREMIUM";

  // Datenreihen für Line-Chart (Impressions aus Backend: aggregiert – hier nur Total anzeigen als Wert-Fläche)
  // Da das Backend derzeit nur Totals liefert, bauen wir eine synthetische Zeitreihe für Anzeige.
  const impressionsSeries = data?.impressions_series || [];
  const engagementRate = data?.engagement?.engagement_rate ?? 0;

  return (
    <div data-testid="watch-analytics-page" className="min-h-screen bg-white">
      <div className="flex items-center gap-3 px-4 h-14 border-b border-[#E5E5E5]">
        <button
          onClick={() => navigate("/studio")}
          data-testid="wa-back"
          className="text-[#0A0A0A]"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-base font-bold flex-1 flex items-center gap-2">
          <BarChart3 size={16} /> Watch-Analytics
        </h1>
        <span
          data-testid="wa-plan-badge"
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0A0A0A] text-white"
        >
          {planCode}
        </span>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6 space-y-6">
        {/* Zeitraum-Toggle */}
        <div className="inline-flex rounded-full border border-[#E5E5E5] p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              data-testid={`wa-window-${w.key}`}
              onClick={() => setWindowDays(w.key)}
              className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${
                windowDays === w.key ? "bg-[#0A0A0A] text-white" : "text-[#666]"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {error && (
          <div
            data-testid="wa-error"
            className="rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
          >
            {String(error)}
          </div>
        )}

        {/* FREE → Upgrade */}
        {!hasBasic && <UpgradeHint feature="watch_analytics_basic" currentPlan={planCode} />}

        {/* KPI-Tiles (Basis) */}
        {hasBasic && data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiTile
              icon={Eye}
              label="Impressions"
              value={data.impressions}
              sub={`${windowDays}-Tage-Zeitraum`}
              testId="wa-kpi-impressions"
            />
            <KpiTile
              icon={Heart}
              label="Likes"
              value={data.engagement?.likes ?? 0}
              testId="wa-kpi-likes"
            />
            <KpiTile
              icon={MessageSquare}
              label="Kommentare"
              value={data.engagement?.comments ?? 0}
              testId="wa-kpi-comments"
            />
            <KpiTile
              icon={TrendingUp}
              label="Engagement-Rate"
              value={`${(engagementRate * 100).toFixed(1)} %`}
              testId="wa-kpi-engagement"
            />
            <KpiTile
              icon={Users}
              label="Neue Follower"
              value={data.new_followers ?? 0}
              testId="wa-kpi-followers"
            />
            <KpiTile
              icon={Sparkles}
              label="Neue Abos"
              value={data.new_subscribers ?? 0}
              testId="wa-kpi-subscribers"
            />
            <KpiTile
              icon={Heart}
              label="Tips (Anzahl)"
              value={data.tips?.count ?? 0}
              testId="wa-kpi-tips-count"
            />
            <KpiTile
              icon={Heart}
              label="Tips (€)"
              value={`${(data.tips?.amount_eur ?? 0).toFixed(2)} €`}
              testId="wa-kpi-tips-amount"
            />
          </div>
        )}

        {/* Engagement-Chart */}
        {hasBasic && data && (
          <div
            data-testid="wa-engagement-chart"
            className="rounded-2xl border border-[#E5E5E5] bg-white p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-3">
              Engagement (Likes vs. Kommentare)
            </p>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart
                  data={[
                    { name: "Likes", value: data.engagement?.likes ?? 0 },
                    { name: "Kommentare", value: data.engagement?.comments ?? 0 },
                    { name: "Tips", value: data.tips?.count ?? 0 },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="name" stroke="#666" fontSize={11} />
                  <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 12 }}
                  />
                  <Bar dataKey="value" fill="#0A0A0A" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Impressions-Timeline (synthetisch aus data.impressions_series wenn vorhanden) */}
        {hasBasic && impressionsSeries.length > 0 && (
          <div
            data-testid="wa-impressions-chart"
            className="rounded-2xl border border-[#E5E5E5] bg-white p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-3">
              Impressions über Zeit
            </p>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={impressionsSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="day" stroke="#666" fontSize={11} />
                  <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 12 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#0A0A0A"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Premium · Retention D1/D7/D30 */}
        {hasBasic && !hasFull && (
          <UpgradeHint feature="watch_analytics_full" currentPlan={planCode} />
        )}

        {hasFull && data?.retention && (
          <div
            data-testid="wa-retention-chart"
            className="rounded-2xl border border-[#E5E5E5] bg-white p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-3">
              Retention (D1 / D7 / D30)
            </p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart
                  data={[
                    { name: "D1", value: Math.round((data.retention.d1 || 0) * 100) },
                    { name: "D7", value: Math.round((data.retention.d7 || 0) * 100) },
                    { name: "D30", value: Math.round((data.retention.d30 || 0) * 100) },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="name" stroke="#666" fontSize={11} />
                  <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(v) => `${v} %`}
                    contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 12 }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    <Cell fill="#0A0A0A" />
                    <Cell fill="#333" />
                    <Cell fill="#666" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Premium · Publish-Time-Heatmap 7×24 */}
        {hasFull && data?.publish_time_heatmap && (
          <div
            data-testid="wa-heatmap"
            className="rounded-2xl border border-[#E5E5E5] bg-white p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-3">
              Publish-Time-Heatmap (Tag × Stunde)
            </p>
            <p className="text-[10px] text-[#999] mb-2">Dunkler = mehr Engagement</p>
            <div className="overflow-x-auto">
              <table className="text-[10px]">
                <thead>
                  <tr>
                    <th className="text-left pr-2 text-[#999]"></th>
                    {Array.from({ length: 24 }).map((_, h) => (
                      <th key={h} className="text-center px-0.5 text-[#999] font-normal">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAY_LABELS.map((day, di) => (
                    <tr key={day}>
                      <td className="pr-2 text-[#666] font-bold">{day}</td>
                      {Array.from({ length: 24 }).map((_, h) => {
                        const v = data.publish_time_heatmap?.[di]?.[h] ?? 0;
                        return (
                          <td
                            key={h}
                            data-testid={`wa-heatmap-cell-${di}-${h}`}
                            title={`${day} ${h}:00 → ${v}`}
                            className="w-4 h-4"
                            style={{ backgroundColor: heatColor(v) }}
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Premium · Source-Attribution */}
        {hasFull && data?.source_attribution && Object.keys(data.source_attribution).length > 0 && (
          <div
            data-testid="wa-attribution-chart"
            className="rounded-2xl border border-[#E5E5E5] bg-white p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-3">
              Traffic-Quellen
            </p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={Object.entries(data.source_attribution).map(([k, v]) => ({ name: k, value: v }))}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    label={(entry) => `${entry.name} (${entry.value})`}
                  >
                    {Object.keys(data.source_attribution).map((_k, idx) => (
                      <Cell
                        key={idx}
                        fill={["#0A0A0A", "#333", "#666", "#999", "#BBB", "#DDD"][idx % 6]}
                      />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
